import React, { useCallback, useEffect, useRef, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonSearchBar from '../../../Components/CommonSearchBar';
import CommonPagination from '../../../Components/CommonPagination';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import StatusBadge from '../../../Components/StatusBadge';
import NoDataPage from '../../../Components/NoDataPage';
import { useConfirm } from '../../../Components/ConfirmModal';
import { useToast } from '../../../Components/CommonToast';
import RiskDetail from '../Pages/RiskDetail'
import AddRisk from '../Forms/AddRisk';
import {
    risk_portal_risks,
    get_by_id_risk,
    delete_risk,
    download_risk_registry_template,
    risk_portal_bulk_import,
} from '../../../services';
import { fontSize, Body } from '../../../Constants/Fonts';
import PageHeading from '../../../Components/PageHeading';
import { useRbac } from '../rbac';
import { currentUserEmail, isOversightRole, visibleToMe } from '../workflow';
// Props:
//   categories / businessUnits / projects — option lists (from useRiskDashboard)
//   role         — current role (owner-email placeholder + All-Orgs visibility)
//   canCreate    — may create / import (create + import/template buttons)
//   canConfigure — may edit / delete rows (Action column)

const STATE_OPTIONS = [
    { value: '', label: 'All States' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'closed', label: 'Closed' },
    { value: 'transferred', label: 'Transferred' },
];

const DEFAULT_CATEGORY_OPTIONS = [
    { value: 'financial', label: 'Financial' },
    { value: 'operational', label: 'Operational' },
    { value: 'technical', label: 'Technical' },
    { value: 'compliance_legal', label: 'Compliance / Legal' },
    { value: 'reputational', label: 'Reputational' },
    { value: 'strategic', label: 'Strategic' },
];

const extractItem = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

// Rating → severity colour token, for the Inherent / Residual score badges.
const RATING_TOKEN = {
    critical: 'var(--color-critical)',
    high: 'var(--color-high)',
    medium: 'var(--color-medium)',
    low: 'var(--color-low)',
};

const scoreBadge = (score, rating, { faded = false } = {}) => {
    const bg = RATING_TOKEN[String(rating || '').toLowerCase()] || 'var(--color-text-muted)';
    return (
        <Body
            as="span"
            color="var(--color-text-inverse)"
            weight="bold"
            style={{
                display: 'inline-block', minWidth: 28, textAlign: 'center', padding: '2px 8px',
                borderRadius: 'var(--radius-sm)', background: bg,
                opacity: faded ? 0.8 : 1, fontVariantNumeric: 'tabular-nums',
            }}
        >
            {score}
        </Body>
    );
};

const muted = (text) => <Body as="span" variant={2} weight="medium" style={{ fontSize: fontSize.sm, color: 'var(--color-text-muted)' }}>{text}</Body>;

export default function RiskRegister({
    categories = [],
    businessUnits = [],
    projects = [],
    role,
    canCreate = false,
    canConfigure = false,
}) {
    const [risks, setRisks] = useState({ results: [], count: 0, page: 1, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [busyBar, setBusyBar] = useState(false);          // import / template in-flight
    const [page, setPage] = useState(0);                    // 0-based (CommonPagination)
    const [allOrgs, setAllOrgs] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filters, setFilters] = useState({ search: '', state: '', risk_type: '', business_unit_id: '', project_id: '' });

    const [showForm, setShowForm] = useState(false);
    const [editingRisk, setEditingRisk] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [detailRiskId, setDetailRiskId] = useState(null);   // clicking a row opens the workflow detail
    const [creatingRisk, setCreatingRisk] = useState(false);  // Create Risk opens the workflow on stage 1

    const fileRef = useRef(null);
    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    // Workflow stages + capability check drive owner-scoped visibility. Oversight
    // roles (admin/manager/ciso) see the whole register; everyone else sees only
    // risks they own or whose CURRENT stage their role owns (see workflow.js).
    const { stages, can } = useRbac();
    const seesAll = isOversightRole(role, can);

    const categoryOptions = [
        { value: '', label: 'All Categories' },
        ...(categories && categories.length
            ? categories.filter(c => c.is_active !== false).map(c => ({ value: c.name || c.id, label: c.name }))
            : DEFAULT_CATEGORY_OPTIONS),
    ];
    const buFilterOptions = [
        { value: '', label: 'All BUs (organization-wide)' },
        ...businessUnits.filter(b => b.is_active).map(b => ({
            value: String(b.id), label: `${b.parent_id ? '↳ ' : ''}${b.name}${b.code ? ` (${b.code})` : ''}`,
        })),
    ];
    const projectFilterOptions = [
        { value: '', label: 'All Projects' },
        ...projects.filter(p => p.is_active !== false).map(p => ({
            value: String(p.id), label: `${p.name}${p.code ? ` (${p.code})` : ''}`,
        })),
    ];

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Oversight roles page server-side. Scoped roles fetch a broad set
            // (page 1, large page_size) so the owner/stage filter + pagination can
            // be applied correctly client-side.
            const params = seesAll ? { page: page + 1 } : { page: 1, page_size: 500 };
            if (filters.search) params.search = filters.search;
            if (filters.state) params.state = filters.state;
            if (filters.risk_type) params.risk_type = filters.risk_type;
            if (filters.business_unit_id) params.business_unit_id = filters.business_unit_id;
            if (filters.project_id) params.project_id = filters.project_id;
            if (allOrgs) params.all_orgs = 'true';
            const d = extractItem(await risk_portal_risks(params));
            if (!d || !d.results) {
                setRisks({ results: [], count: 0, page: 1, pages: 1 });
                return;
            }
            if (seesAll) {
                setRisks(d);
                return;
            }
            // Owner-scoped: keep only risks this user owns or whose current stage
            // their role owns, then paginate the filtered set client-side.
            const email = currentUserEmail();
            const visible = d.results.filter((r) => visibleToMe(r, { role, email, stages }));
            const pageSize = 25;
            const start = page * pageSize;
            setRisks({
                results: visible.slice(start, start + pageSize),
                count: visible.length,
                page: page + 1,
                pages: Math.ceil(visible.length / pageSize) || 1,
            });
        } catch (e) {
            console.error('Load risks failed', e);
            setRisks({ results: [], count: 0, page: 1, pages: 1 });
            notify('Failed to load risks', 'error');
        } finally {
            setLoading(false);
        }
    }, [page, filters, allOrgs, notify, seesAll, role, stages]);

    useEffect(() => { load(); }, [load]);

    // Dropdowns / All-Orgs apply immediately and reset to page 1; the search box
    // applies on Enter / the Search button.
    const applyFilter = (patch) => { setFilters(f => ({ ...f, ...patch })); setPage(0); };
    const runSearch = () => { setFilters(f => ({ ...f, search: searchText })); setPage(0); };

    const openCreate = () => setCreatingRisk(true);
    const closeForm = () => { setShowForm(false); setEditingRisk(null); };

    const handleEdit = async (row) => {
        setEditLoading(true);
        try {
            const raw = extractItem(await get_by_id_risk({ id: row.id }));
            setEditLoading(false);
            if (!raw) { notify('Could not load risk details', 'error'); return; }
            setEditingRisk(raw);
            setShowForm(true);
        } catch (e) {
            setEditLoading(false);
            console.error('Load risk for edit failed', e);
            notify('Could not load risk details', 'error');
        }
    };

    const handleDelete = async (row) => {
        const ok = await confirm({
            title: 'Delete risk', message: `Delete ${row.risk_id || 'this risk'}? This can't be undone.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        try {
            await delete_risk({ id: row.id });
            notify(`Risk ${row.risk_id || ''} deleted`.trim(), 'success');
            load();
        } catch (e) {
            console.error('Delete risk failed', e);
            notify('Failed to delete risk', 'error');
        }
    };

    const handleDownloadTemplate = async () => {
        setBusyBar(true);
        try {
            const response = await download_risk_registry_template({ responseType: 'blob' });
            const blobData = response?.statusCode;
            if (blobData?.type === 'application/json') {
                const json = JSON.parse(await blobData.text());
                notify(json?.error?.message || json?.message || 'Template download failed', 'error');
                return;
            }
            const blob = new Blob([blobData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'risk-registry-template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Template download failed', e);
            notify('Template download failed', 'error');
        } finally {
            setBusyBar(false);
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusyBar(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const d = extractItem(await risk_portal_bulk_import(fd));
            notify(`${d?.created || 0} risks imported, ${d?.skipped || 0} skipped`, d?.skipped ? 'warning' : 'success');
            load();
        } catch (err) {
            console.error('Import failed', err);
            notify('Import failed: ' + (err?.response?.data?.error_msg || err?.response?.data?.response_data?.message || err.message || 'Unknown error'), 'error');
        } finally {
            setBusyBar(false);
        }
    };

    const handleExportCsv = () => {
        const rows = (risks.results || []).map(r =>
            [r.risk_id, `"${(r.risk || '').replace(/"/g, '""')}"`, r.risk_type, r.state, r.inherent_score || '', r.residual_score || '', r.risk_owner, r.target_date || ''].join(','));
        const csv = 'Risk ID,Description,Type,State,Inherent Score,Residual Score,Owner,Target Date\n' + rows.join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'risk_registry_export.csv';
        a.click();
    };

    const showAllOrgs = role === 'risk_admin' || role === 'ciso';

    const HEADERS = [
        'Risk ID', 'Description', 'Business Unit', 'Project', 'Type', 'State',
        'Residual', 'Owner', ...(canConfigure ? ['Action'] : []),
    ];

    const tableRows = (risks.results || []).map((r) => ({
        data: [
            <Body as="span" color="var(--color-primary)" weight="semibold">{r.risk_id || '—'}</Body>,
            // Plain string → CommonTable's ExpandableCell applies the 30/60 rule.
            r.risk || '—',
            r.business_unit_name ? <Body as="span" color="var(--color-text-primary)">{r.business_unit_name}</Body> : muted('org-level'),
            r.project_name ? <Body as="span" color="var(--color-text-primary)">{r.project_name}</Body> : muted('-'),
            <Body as="span" className="rp1_badge rp1_badge--info">{(r.risk_type || '').replace(/_/g, ' ') || '—'}</Body>,
            <StatusBadge status={r.state} label={r.state_label} />,
            // r.assessed ? scoreBadge(r.inherent_score, r.inherent_rating) : muted('-'),
            r.assessed ? scoreBadge(r.residual_score, r.residual_rating, { faded: true }) : muted('-'),
            <Body as="span" color="var(--color-text-secondary)">{r.risk_owner || '-'}</Body>,
            ...(canConfigure ? [(
                // stopPropagation so the action buttons don't also trigger the
                // row-click (open detail). CommonActionMenu collapses Edit/Delete
                // into a single ⋮ menu (inlineThreshold=2).
                <Body as="span" onClick={(e) => e.stopPropagation()}>
                    <CommonActionMenu
                        inlineThreshold={2}
                        actions={[
                            { label: 'Edit', variant: 'edit', onClick: () => handleEdit(r) },
                            { label: 'Delete', danger: true, onClick: () => handleDelete(r) },
                        ]}
                    />
                </Body>
            )] : []),
        ],
    }));

    // A clicked row swaps the list for the single-risk workflow screen; Back
    // returns and refetches (so any stage/assessment change is reflected).
    // Create Risk opens the same screen with no risk yet (stage 1 = the form).
    if (detailRiskId != null || creatingRisk) {
        return (
            <RiskDetail
                riskId={creatingRisk ? null : detailRiskId}
                role={role}
                categories={categories}
                businessUnits={businessUnits}
                projects={projects}
                onBack={() => { setCreatingRisk(false); setDetailRiskId(null); load(); }}
            />
        );
    }

    return (
        <div className="rp1_register rp1_widget">
            {/* Toolbar — title + create / template / import / export */}
            <div className="rp1_register_toolbar">
               <PageHeading title='Risk Register' />
                <div className="rp1_register_actions">
                    {showAllOrgs && (
                        <label className="rp1_register_checkbox">
                            <input type="checkbox" checked={allOrgs} onChange={(e) => { setAllOrgs(e.target.checked); setPage(0); }} />
                            <Body as="span">All Organizations</Body>
                        </label>
                    )}
                    {canCreate && <CommonButton variant="secondary" text="Download Template" onClick={handleDownloadTemplate} disable={busyBar} />}
                    {canCreate && (
                        <>
                            <CommonButton variant="secondary" text="Import Excel/CSV" onClick={() => fileRef.current?.click()} disable={busyBar} />
                            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
                        </>
                    )}
                    <CommonButton variant="secondary" text="Export CSV" onClick={handleExportCsv} />
                    {canCreate && <CommonButton text="+ Create Risk" onClick={openCreate} />}

                </div>
            </div>

            {/* Filters */}
            <div className="rp1_register_filters">
                <CommonSearchBar value={searchText} onChange={setSearchText} onEnter={runSearch} placeholder="Search risks..." />
                <CommonDropdown value={filters.state} onChange={(v) => applyFilter({ state: v })} options={STATE_OPTIONS} ariaLabel="State" minWidth={150} />
                <CommonDropdown value={filters.risk_type} onChange={(v) => applyFilter({ risk_type: v })} options={categoryOptions} ariaLabel="Category" minWidth={170} />
                <CommonDropdown value={filters.business_unit_id} onChange={(v) => applyFilter({ business_unit_id: v })} options={buFilterOptions} ariaLabel="Business unit" minWidth={220} />
                <CommonDropdown value={filters.project_id} onChange={(v) => applyFilter({ project_id: v })} options={projectFilterOptions} ariaLabel="Project" minWidth={180} />
                <CommonButton text="Search" onClick={runSearch} />
            </div>

            {/* Table — loader overlay while fetching, NoDataPage (inside the table) when empty */}
            <div className="rp1_register_table">
                {loading && <CommonLoader overlay label="Loading risks…" />}
                {tableRows.length === 0 ? (
                    <NoDataPage hint="Risks appear here once created." />
                ) : (
                    <CommonTable
                        headers={HEADERS}
                        rows={tableRows}
                        noPadding
                        noScroll
                        onRowClick={(_row, i) => setDetailRiskId((risks.results || [])[i]?.id)}
                    />
                )}
            </div>

            {/* Pagination + count */}
            {risks.pages > 1 && (
                <div className="rp1_register_pagination">
                    <Body as="span" variant={2} weight="medium" style={{ fontSize: fontSize.sm, color: 'var(--color-text-muted)' }}>
                        Page {risks.page} of {risks.pages} ({risks.count} total)
                    </Body>
                    <CommonPagination
                        pageCount={risks.pages}
                        forcePage={page}
                        handlePageClick={({ selected }) => setPage(selected)}
                    />
                </div>
            )}

            {/* Create / Edit modal */}
            <AddRisk
                show={showForm}
                onHide={closeForm}
                editingRisk={editingRisk}
                categories={categories}
                businessUnits={businessUnits}
                projects={projects}
                role={role}
                onSaved={(data) => {
                    const wasEdit = !!editingRisk;
                    const rid = data?.risk_id || editingRisk?.risk_id || '';
                    closeForm();
                    load();
                    notify(`Risk ${rid} ${wasEdit ? 'updated' : 'created'}`.replace('  ', ' ').trim(), 'success');
                    // After creating a new risk, open its detail page directly.
                    if (!wasEdit && data?.id != null) setDetailRiskId(data.id);
                }}
                onError={(msg) => notify(msg || 'Failed to save risk', 'error')}
            />

            {editLoading && <CommonLoader fullPage label="Loading risk…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
